import { FieldProps, FormikProps, getIn } from 'formik';
import _ from 'lodash';
import * as React from 'react';
import { BaseInput, IBaseInputProps } from '../../input';
import * as S from './input.styles';

type InputFieldProps = IBaseInputProps &
    FieldProps & {
        onChange?: (value: string, form: FormikProps<any>) => Promise<void> | void;
        postfixComponent?: React.ReactElement | ((value: string) => React.ReactElement);
    };

/**
 * Wrapper around InputField to be able to work with Form components
 */
export const InputField = React.memo<InputFieldProps>(props => {
    const {
        field: { name, onBlur, onChange, value: fieldValue },
        form: { errors, touched: formTouched },
        postfixComponent,
        placeholder,
        ...otherProps
    } = props;

    const [stateValue, setStateValue] = React.useState(fieldValue);
    const error = getIn(errors, name);
    const touched = getIn(formTouched, name);

    const errorMessage: string | undefined = touched && error ? error : undefined;

    // Update value if new fieldValue passed
    React.useEffect(() => {
        setStateValue(fieldValue);
    }, [fieldValue]);

    const value = _.isNil(stateValue) ? '' : stateValue; // avoid null or undefined values

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        setStateValue(e.target.value);
    };

    const handleBlur = (e: React.ChangeEvent<any>): void => {
        onBlur(e); // To set 'touched'
        props.onChange?.(e.target.value, props.form);
        onChange(e);
    };

    const postfix = _.isFunction(postfixComponent) ? postfixComponent(stateValue) : postfixComponent;

    return (
        <S.Container>
            <S.InputContainer>
                <BaseInput
                    value={value}
                    placeholder={placeholder}
                    {...otherProps}
                    errorMessage={errorMessage}
                    onChange={handleChange} // Manually trigger onchange only on blue for solving performance issues
                    name={name}
                    onBlur={handleBlur}
                />
            </S.InputContainer>
            <S.PostfixContainer>{postfix}</S.PostfixContainer>
        </S.Container>
    );
});
